import React, { type SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import assign from 'lodash/assign';
import Button from '@atlaskit/button/new';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { type TerminalClause, OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import type { JqlData } from '@atlassian/jira-jql-utils/src/utils/types.tsx';
import {
	BetweenComponent,
	BetweenDateTimeComponent,
	InRangeComponent,
	IsNotSetComponent,
	NowOverdue,
	TimeInputComponent,
} from '../common/constants.tsx';
import type {
	DateTimePickerValue,
	DateTimeValue,
	DateTimeVariation,
	TypeConfig,
} from '../common/types.tsx';
import { removeSurroundingQuotes } from '../common/utils/index.tsx';
import BetweenDateTime from './between-date-time/index.tsx';
import Between from './between-dates/index.tsx';
import InRangeOf from './in-range-of-time/index.tsx';
import { Radio } from './input-group/index.tsx';
import TimeInput from './time-input/index.tsx';
import { DateTypeConfig, DueDateTypeConfig, DateTimeTypeConfig } from './utils/index.tsx';
import messages, { messageLookup } from './utils/messages.tsx';

const hasValue = (
	dateTimePickerValue?: DateTimePickerValue & {
		type: string;
	},
): false | DateTimePickerValue => {
	if (dateTimePickerValue) {
		const { type, value } = dateTimePickerValue;
		if (type === 'now_overdue') {
			return { type, value };
		}

		if (value === null) {
			return false;
		}

		switch (type) {
			case 'more_than':
			case 'due_in_next':
			case 'within_the_last':
				return value !== undefined && Boolean(value.value1 && value.value2)
					? { value, type }
					: false;
			case 'between':
			case 'between_date_time':
			case 'in_range':
				return value !== undefined && Boolean(value.value1 || value.value2)
					? { value, type }
					: false;
			default:
				return false;
		}
	}
	return false;
};

const getFilterTypes = (name = ''): TypeConfig[] => {
	if (name === 'DueDate') {
		return DueDateTypeConfig;
	}
	if (name === 'DateTimeRange') {
		return DateTimeTypeConfig;
	}
	return DateTypeConfig;
};
type Props = {
	fieldLabel: string | undefined;
	variation: DateTimeVariation;
	value: DateTimePickerValue | undefined;
	onClosePopup: () => void;
	onChange: (value: DateTimePickerValue | undefined) => void;
	onSendOptionAnalytics?: (arg1: { timeConstraints: string | undefined }) => void;
};

const getDateInput = (
	componentType?: string,
):
	| typeof Between
	| typeof BetweenDateTime
	| typeof TimeInput
	| typeof TimeInput
	| typeof InRangeOf
	| null => {
	switch (componentType) {
		case BetweenComponent:
			return Between;
		case BetweenDateTimeComponent:
			return BetweenDateTime;
		case InRangeComponent:
			return InRangeOf;
		case TimeInputComponent:
			return TimeInput;
		default:
			return null;
	}
};

const DateTimeView = ({
	fieldLabel,
	value: valueProp,
	variation,
	onChange,
	onClosePopup,
	onSendOptionAnalytics,
}: Props) => {
	const intl = useIntl();
	const [selectedTypeConfig, setSelectedTypeConfig] = useState<TypeConfig | undefined>(
		valueProp !== undefined && valueProp.type
			? getFilterTypes(variation).filter(
					(filterType) => valueProp && filterType.type === valueProp.type,
				)[0]
			: undefined,
	);

	const [value, setValue] = useState<{
		[key: string]: DateTimeValue;
	}>({
		between: {
			value1: '',
			value2: '',
		},
		between_date_time: {
			value1: '',
			value2: '',
		},
		in_range: {
			value1: '',
			value2: '',
		},
		within_the_last: {
			value1: '',
			value2: '',
		},
		more_than: {
			value1: '',
			value2: '',
		},
		due_in_next: {
			value1: '',
			value2: '',
			excludeOverdue: true,
		},
		...(valueProp && {
			[`${valueProp.type}`]: {
				...valueProp.value,
			},
		}),
	});

	const onChangeCheckbox = useCallback(
		(event: SyntheticEvent<HTMLInputElement>) => {
			const type = event.currentTarget.value;

			const typeConfig = getFilterTypes(variation).find((t) => t.type === type);

			setSelectedTypeConfig(typeConfig);
		},
		[variation],
	);

	const onChangeInput = useCallback(
		(val: DateTimeValue) => {
			setValue(assign({}, value, selectedTypeConfig ? { [selectedTypeConfig.type]: val } : {}));
		},
		[selectedTypeConfig, value],
	);

	const errorMessage = useMemo(() => {
		let message;

		getFilterTypes(variation).forEach((m) => {
			const isCurrent = selectedTypeConfig ? m.type === selectedTypeConfig.type : false;
			const currentValue = selectedTypeConfig && value[selectedTypeConfig.type];

			if (m.getErrorMessage && isCurrent) {
				message = m.getErrorMessage(intl, currentValue);
			}
		});

		return message;
	}, [intl, selectedTypeConfig, value, variation]);

	const onUpdate = useCallback(() => {
		if (errorMessage !== undefined) return;

		const val: DateTimeValue | undefined =
			selectedTypeConfig && selectedTypeConfig.type !== NowOverdue
				? value[selectedTypeConfig.type]
				: undefined;

		const datePickerValue: DateTimePickerValue | undefined =
			selectedTypeConfig !== undefined
				? {
						type: selectedTypeConfig.type,
						value: selectedTypeConfig.componentType !== IsNotSetComponent ? val : undefined,
					}
				: undefined;

		onChange(datePickerValue);
		if (typeof onClosePopup === 'function') {
			onClosePopup();
		}

		onSendOptionAnalytics &&
			onSendOptionAnalytics({
				timeConstraints: datePickerValue?.type,
			});
	}, [errorMessage, onChange, onClosePopup, onSendOptionAnalytics, selectedTypeConfig, value]);

	const handleOnClick = useCallback(
		(event: SyntheticEvent<HTMLElement>) => {
			event.preventDefault();

			onUpdate();
		},
		[onUpdate],
	);

	const handleOnKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'Enter') {
				event.stopPropagation();
				event.preventDefault();

				onUpdate();
			}
		},
		[onUpdate],
	);

	const DateInputType = selectedTypeConfig
		? getDateInput(selectedTypeConfig.componentType)
		: undefined;

	return (
		<StyledDialogInner onKeyDown={handleOnKeyDown}>
			<Box as="fieldset" xcss={fieldsetStyles}>
				<Box as="legend" xcss={legendStyles}>
					{intl.formatMessage(messages.legendText, { fieldLabel })}
				</Box>
				{getFilterTypes(variation).map((m) => {
					const isCurrent = !!selectedTypeConfig && m.type === selectedTypeConfig.type;
					const currentValue = selectedTypeConfig && value[selectedTypeConfig.type];
					const messageDescriptor = messageLookup[m.label];
					return (
						<StyledOptionContainer key={m.type}>
							<Radio
								checked={isCurrent}
								name="mode"
								onChange={onChangeCheckbox}
								value={m.type}
								testId={`jql-builder-basic-datetime.ui.radio.${m.type}`}
							>
								{messageDescriptor ? intl.formatMessage(messageDescriptor) : null}
							</Radio>
							{isCurrent && m.hasInput && currentValue && DateInputType && (
								<DateInputType
									isInvalid={isCurrent && errorMessage !== undefined}
									typeConfig={m}
									onChange={onChangeInput}
									value={currentValue}
								/>
							)}
							{isCurrent && errorMessage !== undefined && (
								<ErrorContainer>
									<ErrorIcon LEGACY_size="small" label="" color={token('color.icon.danger')} />
									{errorMessage}
								</ErrorContainer>
							)}
						</StyledOptionContainer>
					);
				})}
			</Box>
			<Box xcss={buttonWrapperStyle}>
				<Button
					appearance="primary"
					testId="jql-builder-basic-datetime.ui.update-button"
					spacing="default"
					isDisabled={errorMessage !== undefined}
					onClick={handleOnClick}
				>
					{intl.formatMessage(messages.updateButtonText)}
				</Button>
			</Box>
		</StyledDialogInner>
	);
};
DateTimeView.whyDidYouRender = true;

export default DateTimeView;

type FormatLabelProps = {
	value: DateTimePickerValue | undefined;
	label: string;
	variation: DateTimeVariation;
	intl: IntlShape;
};

export const formatLabel = ({ intl, variation, label, value: field }: FormatLabelProps) => {
	const typeConfig = field
		? getFilterTypes(variation).find((f) => f.type === field.type)
		: undefined;

	if (typeConfig === undefined) {
		return (
			<Text weight="medium" color="inherit">
				{label}
			</Text>
		);
	}

	const value = typeConfig.getFormattedValue(intl, field && field.value);
	return value !== '' ? (
		<Text weight="medium" color="inherit">
			<Text as="strong">{label}: </Text>
			{typeConfig.getFormattedValue(intl, field && field.value)}
		</Text>
	) : (
		<Text weight="medium" color="inherit">
			{label}
		</Text>
	);
};

export const getTooltip = ({
	intl,
	variation,
	value: field,
}: FormatLabelProps): string | undefined => {
	const typeConfig = field
		? getFilterTypes(variation).find((f) => f.type === field.type)
		: undefined;

	if (typeConfig === undefined || !typeConfig.getTooltip) {
		return undefined;
	}

	return typeConfig.getTooltip(intl, field && field.value);
};

export const getJqlInfo = (
	dateTimePickerValue: DateTimePickerValue | undefined,
	variation: DateTimeVariation,
): JqlData | undefined => {
	if (dateTimePickerValue) {
		const { type, value } = dateTimePickerValue;
		const typeConfig = getFilterTypes(variation).find((f) => f.type === type);
		const hasValuePresent = hasValue({ type, value });
		if (!hasValuePresent) {
			return undefined;
		}

		if (typeConfig) {
			if (typeConfig.type === NowOverdue) {
				return typeConfig.getJqlData();
			}
			if (value) {
				return typeConfig.getJqlData(value);
			}
		}
	}
	return undefined;
};

export const getValueFromJqlData = (
	variation: DateTimeVariation,
	jqlData?: JqlData,
): DateTimePickerValue | undefined => {
	const possibleValues = getFilterTypes(variation)
		.map((typeConfig) => {
			const value = typeConfig.getValueFromJqlData(jqlData);
			if (value) {
				return {
					type: typeConfig.type,
					value,
				};
			}
			return undefined;
		})
		.filter(Boolean);
	if (possibleValues.length > 0) {
		return possibleValues[0];
	}
	return undefined;
};

export const getErrorMessage = (
	intl: IntlShape,
	dateTimePickerValue: DateTimePickerValue | undefined,
	variation: DateTimeVariation,
): string | undefined => {
	if (dateTimePickerValue) {
		const typeConfig = getFilterTypes(variation).filter(
			(filterType) => filterType.type === dateTimePickerValue.type,
		)[0];
		if (dateTimePickerValue && typeConfig && typeConfig.getErrorMessage) {
			return typeConfig.getErrorMessage(intl, dateTimePickerValue.value);
		}
	}
	return undefined;
};

export const getValueFromClauses = (
	variation: DateTimeVariation,
	primaryClause?: TerminalClause,
	secondaryClause?: TerminalClause,
): DateTimePickerValue | undefined => {
	const jqlData: JqlData | undefined =
		primaryClause === undefined && secondaryClause === undefined
			? undefined
			: {
					primaryClause:
						primaryClause !== undefined &&
						primaryClause.operator &&
						primaryClause.operand &&
						primaryClause.operand.operandType === OPERAND_TYPE_VALUE
							? {
									operator: primaryClause.operator.value,
									operand: removeSurroundingQuotes(primaryClause.operand.text),
								}
							: undefined,
					secondaryClause:
						secondaryClause !== undefined &&
						secondaryClause.operator &&
						secondaryClause.operand &&
						secondaryClause.operand.operandType === OPERAND_TYPE_VALUE
							? {
									operator: secondaryClause.operator.value,
									operand: removeSurroundingQuotes(secondaryClause.operand.text),
								}
							: undefined,
				};
	return getValueFromJqlData(variation, jqlData);
};

const fieldsetStyles = xcss({
	border: 'none',
	margin: 'space.0',
	paddingBlockStart: 'space.200',
	paddingBlockEnd: 'space.100',
	paddingInlineStart: 'space.150',
	paddingInlineEnd: 'space.150',
});
const legendStyles = xcss({
	width: '1px',
	height: '1px',
	padding: 'space.0',
	position: 'absolute',
	border: 'none',
	clip: 'rect(1px, 1px, 1px, 1px)',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});

const buttonWrapperStyle = xcss({
	borderTop: `1px solid ${token('color.border')}`,
	paddingInlineStart: 'space.150',
	paddingBlockStart: 'space.075',
	paddingBlockEnd: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const StyledOptionContainer = styled.div({
	marginBottom: token('space.200'),
	minHeight: '26px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-of-type': {
		marginBottom: token('space.0'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ErrorContainer = styled.div({
	marginLeft: token('space.300'),
	marginTop: token('space.050'),
	color: token('color.text.danger'),
	display: 'flex',
	alignItems: 'center',
	font: token('font.body.UNSAFE_small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const StyledDialogInner = styled.div({
	width: '380px',
});
